import { useMutation, useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../api';
import { can, type Me } from '../auth/me';
import { ConflictNotice } from '../ui/ConflictNotice';
import type { Settlement } from './types';

type ContractOption = { id: string; number: string; title: string; state: string };

/**
 * "Umowa: UD/1/2026" on a settlement's card (F44): the contract the person is paid under. Whoever edits all
 * settlements and sees contracts picks one of the person's contracts (or none).
 */
export function SettlementContract({ settlement, me, mayEdit, onChanged }: { settlement: Settlement; me: Me; mayEdit: boolean; onChanged: () => void }) {
  const seeContracts = can(me, 'contracts.contracts', 'view');
  const editable = mayEdit && seeContracts;
  const options = useQuery({
    queryKey: ['contracts', 'person', settlement.personId],
    enabled: editable,
    queryFn: () => api<{ contracts: ContractOption[] }>(`/api/contracts?personId=${settlement.personId}`),
  });
  const save = useMutation({
    mutationFn: (contractId: string | null) => api<Settlement>(`/api/settlements/${settlement.id}/contract`, { method: 'PUT', body: { contractId, version: settlement.version } }),
    onSuccess: onChanged,
  });
  const label = settlement.contractNumber ? (
    seeContracts ? <Link to={`/finanse/umowy/${settlement.contractId}`}>{settlement.contractNumber}</Link> : settlement.contractNumber
  ) : settlement.contractId ? (
    <span className="muted">umowa usunięta</span>
  ) : (
    <span className="muted">brak</span>
  );
  return (
    <div className="stl-contract">
      {editable && options.data ? (
        <label className="field stl-contract-pick">
          <span>Umowa</span>
          <select value={settlement.contractId ?? ''} disabled={save.isPending} onChange={(e) => save.mutate(e.target.value || null)}>
            <option value="">— bez umowy —</option>
            {options.data.contracts
              .filter((c) => c.state === 'wygenerowana' || c.state === 'podpisana' || c.id === settlement.contractId)
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {c.number} · {c.title}
                </option>
              ))}
          </select>
        </label>
      ) : (
        <p>Umowa: {label}</p>
      )}
      {save.error instanceof ApiError && save.error.status === 409 && (
        <ConflictNotice
          onReload={() => {
            save.reset();
            onChanged();
          }}
        />
      )}
      {save.error && !(save.error instanceof ApiError && save.error.status === 409) && (
        <p className="alert" role="alert">
          {save.error instanceof Error ? save.error.message : 'Nie udało się'}
        </p>
      )}
    </div>
  );
}
